import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { OnboardingData } from '@/lib/types';

const GOAL_LABELS: Record<string, string> = {
  hypertrophy: 'Hipertrofia',
  weight_loss: 'Emagrecimento',
  conditioning: 'Condicionamento',
  health: 'Saúde geral',
};

export interface PersonalData {
  full_name: string;
  phone?: string;
  birth_date?: string;
  height_cm?: number;
  weight_kg?: number;
  sex?: 'M' | 'F' | 'O';
}

export function useOnboarding(profileId: string) {
  const [studentId, setStudentId] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    if (!profileId) return;
    supabase
      .from('students')
      .select('id')
      .eq('profile_id', profileId)
      .single()
      .then(({ data, error }) => {
        if (error) { setFetchError(true); return; }
        if (data) setStudentId(data.id);
      });
  }, [profileId]);

  async function saveOnboarding(
    onboardingData: OnboardingData,
    personalData: PersonalData,
  ): Promise<void> {
    if (!studentId) throw new Error('studentId is required');

    const { error: studentErr } = await supabase
      .from('students')
      .update({
        full_name: personalData.full_name,
        phone: personalData.phone ?? null,
        birth_date: personalData.birth_date ?? null,
        height_cm: personalData.height_cm ?? null,
        weight_kg: personalData.weight_kg ?? null,
        sex: personalData.sex ?? null,
        goal: GOAL_LABELS[onboardingData.goal] ?? onboardingData.goal,
        health_notes: onboardingData.limitations?.trim() || null,
        onboarding_data: onboardingData,
        onboarding_completed: true,
        status: 'waiting',
      })
      .eq('id', studentId);
    if (studentErr) throw new Error(studentErr.message);

    if (profileId) {
      const { error: profileErr } = await supabase
        .from('profiles')
        .update({ full_name: personalData.full_name })
        .eq('id', profileId);
      if (profileErr) throw new Error(profileErr.message);
    }

    // Fire-and-forget: o aluno não espera a IA terminar (~10-20s). Mas agora
    // logamos qualquer falha — funcional (error no retorno) OU de rede. Antes
    // era `.catch(() => {})`, que engolia tudo: se a função quebrava, a sugestão
    // simplesmente nunca aparecia pro personal, sem nenhum rastro.
    supabase.functions
      .invoke('generate-onboarding-plan', { body: { student_id: studentId } })
      .then(({ error: fnError }) => {
        if (fnError) console.warn('[onboarding] generate-onboarding-plan falhou:', fnError);
      })
      .catch((err) => console.warn('[onboarding] generate-onboarding-plan erro de rede:', err));
  }

  return { saveOnboarding, studentId, fetchError };
}
